import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import type { LanguageOption } from "../i18n/languageCatalog";

type LanguageChooserProps = {
  closeLabel: string;
  dialogTitle: string;
  filterAriaLabel: string;
  filterPlaceholder: string;
  onSelect: (tag: string) => void;
  options: LanguageOption[];
  selectedTag: string;
  triggerAriaLabel: string;
  triggerClassName?: string;
  triggerContent: ReactNode;
  triggerTitle?: string;
};

export const LanguageChooser = ({
  closeLabel,
  dialogTitle,
  filterAriaLabel,
  filterPlaceholder,
  onSelect,
  options,
  selectedTag,
  triggerAriaLabel,
  triggerClassName = "",
  triggerContent,
  triggerTitle,
}: LanguageChooserProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [languageFilter, setLanguageFilter] = useState<string>("");

  const filteredLanguages = useMemo(() => {
    const query = languageFilter.trim().toLowerCase();
    if (query.length === 0) {
      return options;
    }

    return options.filter((option) => {
      return option.englishLabel.toLowerCase().includes(query) || option.nativeLabel.toLowerCase().includes(query);
    });
  }, [languageFilter, options]);

  return (
    <>
      <button
        aria-label={triggerAriaLabel}
        className={triggerClassName}
        onClick={() => {
          setLanguageFilter("");
          setIsOpen(true);
        }}
        title={triggerTitle ?? triggerAriaLabel}
        type="button"
      >
        {triggerContent}
      </button>

      <section
        aria-hidden={!isOpen}
        className={`language-overlay${isOpen ? " is-open" : ""}`}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            setIsOpen(false);
          }
        }}
      >
        <div
          aria-labelledby="language-title"
          aria-modal="true"
          className="language-panel"
          role="dialog"
        >
          <div className="language-header">
            <h2
              className="language-title"
              id="language-title"
            >
              {dialogTitle}
            </h2>
            <button
              aria-label={closeLabel}
              className="language-close"
              onClick={() => setIsOpen(false)}
              type="button"
            >
              ✕
            </button>
          </div>
          <input
            aria-label={filterAriaLabel}
            autoComplete="off"
            className="language-filter"
            onChange={(event) => setLanguageFilter(event.target.value)}
            placeholder={filterPlaceholder}
            type="search"
            value={languageFilter}
          />
          <div className="language-grid">
            {filteredLanguages.map((option) => (
              <button
                className={`language-option${option.tag === selectedTag ? " is-active" : ""}`}
                key={option.tag}
                onClick={() => {
                  onSelect(option.tag);
                  setIsOpen(false);
                }}
                type="button"
              >
                {option.nativeLabel}
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
